<script setup lang="ts">
import { computed } from 'vue'

import Equipment from './Equipment.vue'
import IngredientGroups from './IngredientGroups.vue'
import InstructionGroups from './InstructionGroups.vue'
import NutritionFacts from './NutritionFacts.vue'
import RecipeMeta from './RecipeMeta.vue'
import RecipeSeoText from './RecipeSeoText.vue'
import AdaSlotMarker from '../../ad-slots/AdaSlotMarker.vue'

import type { RecipeParagraph } from '../../../utils/paragraph-schema/paragraphs'
import type { ComposedPageMeta } from '@xcel-next/composer-schema'
import type { Brand } from '@xcel-next/core/enums'

const props = defineProps<{
  recipe?: RecipeParagraph['recipe'],
  brand?: Brand,
  pageMeta?: ComposedPageMeta
}>()

const hasNutritionFacts = computed(() => {
  const nutrition = props.recipe?.nutritionFacts
  return !!(nutrition?.calories || nutrition?.carbohydrate || nutrition?.protein || nutrition?.fat)
})

// InstructionGroups expects a required `instructions` array, recipe schema has it optional
const instructionGroups = computed(() => props.recipe?.instructionGroups?.map(group => ({
  ...group,
  instructions: group.instructions ?? []
})))
</script>

<template>
  <div class="bx-typo--recipe bx-recipe">
    <slot name="cookbook-button-bar" />
    <AdaSlotMarker v-bind="{ aboveParagraph: { index: 1, type: 'recipeButtonBar' } }" />
    <RecipeMeta :recipe="recipe"
                :page-meta="pageMeta" />
    <AdaSlotMarker v-bind="{ aboveParagraph: { index: 1, type: 'recipeMeta' } }" />
    <RecipeSeoText v-if="recipe?.seoText?.html"
                   :seo-text-html="recipe.seoText.html" />
    <AdaSlotMarker v-bind="{ aboveParagraph: { index: 1, type: 'recipeSeoText' } }" />
    <IngredientGroups v-if="recipe?.ingredientGroups"
                      :ingredient-groups="recipe.ingredientGroups"
                      :serving="recipe.serving" />
    <Equipment v-if="recipe?.equipment?.length"
               :equipment="recipe.equipment" />
    <InstructionGroups v-if="instructionGroups"
                       :instruction-groups="instructionGroups"
                       :brand="brand" />
    <NutritionFacts v-if="hasNutritionFacts"
                    :nutrition-facts="recipe?.nutritionFacts" />
  </div>
</template>
